import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { uploadFileOffline } from "@/lib/uploadOffline";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UploadDropzone from "@/components/UploadDropzone";
import { File as FileIcon, FileText, FileSpreadsheet, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function FilesPanel({ project, kind, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const items = (kind === "screenshot" ? project.screenshots : project.test_docs) || [];

  useEffect(() => {
    const url = kind === "screenshot" ? "/templates/screenshot-categories" : "/templates/test-doc-categories";
    api.get(url).then((r) => {
      setCategories(r.data.categories);
      setCategory((c) => c || r.data.categories[0]);
    });
  }, [kind]);

  const upload = async (files) => {
    if (!category) { toast.error("Select a category first"); return; }
    setBusy(true);
    try {
      let queuedCount = 0;
      let uploadedCount = 0;
      for (const file of files) {
        const r = await uploadFileOffline({
          projectId: project.id,
          file,
          fields: { kind, category },
          timeout: 180_000,
        });
        if (r.queued) queuedCount += 1;
        else uploadedCount += 1;
      }
      if (uploadedCount > 0 && queuedCount === 0) {
        toast.success(`Uploaded ${uploadedCount} file(s)`);
      } else if (queuedCount > 0 && uploadedCount === 0) {
        toast.info(`${queuedCount} file(s) queued — will upload when back online`);
      } else {
        toast.success(`${uploadedCount} uploaded, ${queuedCount} queued for sync`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    toast.success("Removed");
    onChanged?.();
  };

  const isImage = (ct) => ct?.startsWith("image/");
  const accept = kind === "screenshot"
    ? "image/*,application/pdf,.xlsx,.xls,.csv"
    : ".pdf,.xlsx,.docx,.png,.jpg,.jpeg";

  return (
    <div className="space-y-4" data-testid={`files-panel-${kind}`}>
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-display font-bold">Category</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-slate-950 border-slate-700 max-w-xs" data-testid={`${kind}-category-select`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <UploadDropzone
            onFiles={upload}
            busy={busy}
            accept={accept}
            label={kind === "screenshot" ? "Drop screenshots, paste, or click" : "Drop test docs (PDF/XLSX/DOCX) or click"}
            hint={kind === "test_doc" ? "AI will extract Site, Pass/Fail, and Test Date when possible." : "PNG, JPG, PDF, or Excel (XLSX / XLS / CSV)."}
            testid={`${kind}-dropzone`}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${kind}-list`}>
        {items.length === 0 && (
          <div className="text-sm text-slate-400 col-span-full">No {kind === "screenshot" ? "screenshots" : "test documents"} yet.</div>
        )}
        {items.map((f) => (
          <Card key={f.id} className="bg-slate-900 border-slate-800" data-testid={`file-card-${f.id}`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded bg-slate-950 border border-slate-800 flex items-center justify-center shrink-0 overflow-hidden">
                  {isImage(f.content_type) ? (
                    <img src={fileUrl(f.id, { size: "thumb" })} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : f.content_type === "application/pdf" ? (
                    <FileText className="w-7 h-7 text-red-400" />
                  ) : /\.(xlsx|xls|csv)$/i.test(f.original_filename || "") ? (
                    <FileSpreadsheet className="w-7 h-7 text-emerald-400" />
                  ) : (
                    <FileIcon className="w-7 h-7 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-display">{f.category}</div>
                  <div className="text-sm font-medium truncate" title={f.original_filename}>{f.original_filename}</div>
                  {f.extraction && (
                    <div className="mt-1.5 text-[11px] space-y-0.5 text-slate-300 font-mono bg-slate-950 border border-slate-800 rounded p-1.5">
                      {Object.entries(f.extraction).slice(0, 4).map(([k, v]) => v ? (
                        <div key={k}><span className="text-slate-500">{k}:</span> {String(v).slice(0, 40)}</div>
                      ) : null)}
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <a href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline" data-testid={`file-open-${f.id}`}>Open</a>
                    <button onClick={() => remove(f.id)} className="text-xs text-red-400 hover:underline inline-flex items-center gap-1" data-testid={`file-delete-${f.id}`}><Trash2 className="w-3 h-3"/>Delete</button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
